// Add Meal sheet — pick a day + slot + source (recipe or quick note),
// hit Save. Pre-fillable so tapping a cell in the grid opens the
// sheet with that day/slot already selected.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type MealSlot, type Recipe } from "../../lib/rpc";

interface AddMealProps {
  open: boolean;
  onClose: () => void;
  onSave: (args: { date: string; slot: MealSlot; value: string; recipeId?: string | null }) => Promise<void>;
  /** Seven ISO dates from the visible week (Monday-first). */
  weekDates: string[];
  /** Pre-selected date when opening from a cell click. */
  prefillDate?: string;
  /** Pre-selected slot when opening from a cell click. */
  prefillSlot?: MealSlot;
}

type Source = "recipe" | "note";

const SUGGESTIONS = ["Leftovers", "Takeout", "Out", "Pizza night", "Whatever"];

function dayName(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString([], { weekday: "short" });
}
function dayDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function AddMealSheet({
  open, onClose, onSave, weekDates, prefillDate, prefillSlot,
}: AddMealProps) {
  const [date, setDate] = useState<string>(prefillDate || weekDates[0]);
  const [slot, setSlot] = useState<MealSlot>(prefillSlot || "dinner");
  const [source, setSource] = useState<Source>("recipe");
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [recipeName, setRecipeName] = useState<string>("");
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setDate(prefillDate || weekDates[0]);
    setSlot(prefillSlot || "dinner");
    setSource("recipe");
    setRecipeId(null);
    setRecipeName("");
    setSearch("");
    setNote("");
    setSaving(false);
  }, [open, prefillDate, prefillSlot, weekDates]);

  // Lazy-load the user's recipes when the sheet opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.recipesList()
      .then((list) => { if (!cancelled) setRecipes(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter((r) => r.name.toLowerCase().includes(q));
  }, [recipes, search]);

  if (!open) return null;

  const canSave = source === "recipe"
    ? !!(recipeId && recipeName)
    : !!note.trim();

  const save = async () => {
    setSaving(true);
    try {
      if (source === "recipe" && recipeId && recipeName) {
        await onSave({ date, slot, value: recipeName, recipeId });
      } else if (source === "note" && note.trim()) {
        await onSave({ date, slot, value: note.trim(), recipeId: null });
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="am-bg" onClick={onClose}>
      <div className="am-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="am-head">
          <span className="am-head-title">Add meal</span>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={13} />
          </button>
        </header>
        <div className="am-body">
          <div>
            <div className="am-label">Day</div>
            <div className="am-day-chips" style={{ marginTop: 6 }}>
              {weekDates.map((d) => (
                <button
                  key={d}
                  data-active={d === date}
                  onClick={() => setDate(d)}
                  type="button"
                >
                  <span>{dayName(d)}</span>
                  <strong>{dayDate(d)}</strong>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="am-label">Meal</div>
            <div className="am-slot-pill" style={{ marginTop: 6 }}>
              {(["breakfast", "lunch", "dinner"] as const).map((s) => (
                <button
                  key={s}
                  data-active={slot === s}
                  onClick={() => setSlot(s)}
                  type="button"
                >
                  {s[0].toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="am-label">Source</div>
            <div className="am-source-tabs" style={{ marginTop: 6 }}>
              <button data-active={source === "recipe"} onClick={() => setSource("recipe")} type="button">
                From recipes
              </button>
              <button data-active={source === "note"} onClick={() => setSource("note")} type="button">
                Quick note
              </button>
            </div>
          </div>

          {source === "recipe" ? (
            <div className="am-recipe-search">
              <input
                type="text"
                className="am-input"
                placeholder="Search recipes…"
                value={search}
                onInput={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
              />
              <div className="am-recipe-list">
                {filtered.length === 0
                  ? <p style={{ color: "var(--text-dim)", padding: "6px 8px", fontSize: 12, fontStyle: "italic", margin: 0 }}>
                      {recipes.length === 0 ? "No recipes yet." : "No matches."}
                    </p>
                  : filtered.map((r) => (
                      <button
                        key={r.id}
                        className="am-recipe-row"
                        data-active={recipeId === r.id}
                        onClick={() => { setRecipeId(r.id); setRecipeName(r.name); }}
                        type="button"
                      >
                        <div className="am-recipe-thumb">
                          {r.imageUrl && <img src={r.imageUrl} alt="" />}
                        </div>
                        <span>{r.name}</span>
                      </button>
                    ))
                }
              </div>
            </div>
          ) : (
            <div>
              <input
                type="text"
                className="am-input"
                placeholder="e.g. Leftover pasta, Takeout, Whatever's fast…"
                value={note}
                onInput={(e) => setNote((e.currentTarget as HTMLInputElement).value)}
                maxLength={200}
                autofocus
              />
              <div className="am-suggestion-chips">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => setNote(s)} type="button">{s}</button>
                ))}
              </div>
            </div>
          )}

          <div className="am-actions">
            <button className="btn-ghost" onClick={onClose} type="button">Cancel</button>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={!canSave || saving}
              type="button"
            >
              <Icon name="check" size={13} />
              <span>{saving ? "Saving…" : "Add to plan"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
